import produce from "immer"

import { InferValueTypes, replaceHistoryState } from "client/utils"
import {
    initPackageSettings,
    initAccountSettings,
    initUserSettings,
    initPackageUsage,
} from "client/store"
import {
    createAccountPermissions,
    createLifecyclePermissions,
} from "client/store/utils"
import * as types from "./types"
import * as actions from "./actions"
import { defineTheme } from "./utils"

type ActionTypes = ReturnType<InferValueTypes<typeof actions>>

export const initUserData: types.UserData = {
    userId: null,
    userName: null,
    userLogo: null,
    userEmail: null,
    accountId: null,
    accountName: null,
    accountLogo: null,
    sessionId: null,
}

const initSystem: types.System = {
    userData: initUserData,
    userGroups: null,
    projectUserGroups: {},

    packageActive: null,
    packageSettings: initPackageSettings,
    packageUsage: initPackageUsage,

    accountSettings: initAccountSettings,
    userSettings: initUserSettings,

    accountPermissions: createAccountPermissions().permissions,
    projectPermissions: {},
    lifecyclePermissions: createLifecyclePermissions().permissions,

    lostServerConnection: false,

    theme: defineTheme(),
    lang: null,
    langReport: null,

    urlOrigin: null,
    modals: [],

    renderContent: false,
    renderProjectContent: false,
}

const systemReducer = produce((
    draft: types.System = initSystem,
    action: ActionTypes = null,
): types.System => {
    let currentModal: types.ModalWithUrl

    switch (action.type) {
        case types.SYSTEM_USER_DATA:
            draft.userData = action.userData
            return draft

        case types.CLEAR_SYSTEM_USER_DATA:
            draft.userData = initUserData
            return draft

        case types.SYSTEM_USER_GROUPS:
            draft.userGroups = action.userGroups
            return draft

        case types.SYSTEM_PROJECT_USER_GROUPS:
            draft.projectUserGroups = {
                ...draft.projectUserGroups,
                [action.projectId]: action.projectUserGroups,
            }
            return draft


        case types.SYSTEM_PACKAGE_ACTIVE:
            draft.packageActive = action.activePackage
            return draft

        case types.SYSTEM_PACKAGE_SETTINGS:
            draft.packageSettings = action.packageSettings
            return draft

        case types.SYSTEM_PACKAGE_USAGE:
            draft.packageUsage = action.packageUsage
            return draft

        case types.SYSTEM_PACKAGE_USAGE_SIL_CALCULATIONS:
            draft.packageUsage.usedCalculations = action.silCalculations
            return draft


        case types.SYSTEM_ACCOUNT_SETTINGS:
            draft.accountSettings = action.settings
            return draft

        case types.SYSTEM_USER_SETTINGS:
            draft.userSettings = action.settings
            return draft


        case types.SYSTEM_ACCOUNT_PERMISSIONS:
            draft.accountPermissions = action.accountPermissions
            return draft

        case types.SYSTEM_PROJECT_PERMISSIONS:
            draft.projectPermissions = {
                ...draft.projectPermissions,
                [action.projectId]: action.projectPermissions,
            }
            return draft

        case types.SYSTEM_LIFECYCLE_PERMISSIONS:
            draft.lifecyclePermissions = action.lifecyclePermissions
            return draft


        case types.SYSTEM_LOST_SERVER_CONNECTION:
            draft.lostServerConnection = true
            return draft


        case types.SYSTEM_THEME:
            draft.theme = action.theme
            return draft

        case types.SYSTEM_LANG:
            draft.lang = action.lang
            return draft

        case types.SYSTEM_LANG_REPORT:
            draft.langReport = action.lang
            return draft


        case types.URL_ORIGIN:
            draft.urlOrigin = action.value
            return draft

        case types.ADD_MODAL:
            draft.modals.push(action.params)
            return draft

        case types.CLOSE_MODAL:
            draft.modals = draft.modals.slice(0, draft.modals.length - 1)
            return draft

        case types.UPDATE_MODAL_URL:
            currentModal = draft.modals[draft.modals.length - 1] as types.ModalWithUrl
            if (currentModal && currentModal.url && action.url) {
                replaceHistoryState(action.url)
                currentModal.url = action.url

                draft.modals = draft.modals.slice(0, draft.modals.length - 1)
                draft.modals.push(currentModal)
            }
            return draft


        case types.RENDER_CONTENT:
            draft.renderContent = action.value
            return draft

        case types.RENDER_PROJECT_CONTENT:
            draft.renderProjectContent = action.value
            return draft

        default:
            return draft
    }
})
export default systemReducer
